import { RailDisclosureIcon } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import type { LucideIcon } from "lucide-react";

/**
 * A workspace sub-section (Canvases / Chats / Files) styled as a normal folder
 * row — same markup as `CanvasFolderRow`/`FilesRailFolderRow` (row height,
 * chevron + icon, hover-reveal kebab, indented children) so an expanded
 * workspace reads as three folders rather than three headed groups. Collapsed
 * by default, like any folder. The hover-reveal group scopes to the header row
 * only (`group/row`), so hovering a child row never reveals this section's kebab.
 */
export function RailSectionFolderRow({
  icon,
  openIcon,
  label,
  menu,
  onContextMenu,
  expansionKey,
  defaultOpen = false,
  children,
  "data-onboarding-target": dataOnboardingTarget,
}: {
  icon: LucideIcon;
  /** Icon while expanded (e.g. `FolderOpen`); falls back to `icon`. */
  openIcon?: LucideIcon;
  label: string;
  /** The section's kebab (a `RailRowMenu`) — revealed via the header row's
   *  `group/row`, i.e. the default `railRowKebabWrapperClassName`. */
  menu?: React.ReactNode;
  onContextMenu?: (e: React.MouseEvent) => void;
  /** Persists the open state under this key so the section survives remounts. */
  expansionKey: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  /** Onboarding spotlight anchor (see `OnboardingTargetId`). */
  "data-onboarding-target"?: string;
}) {
  const [open, setOpen] = useRailExpansion(expansionKey, defaultOpen);

  return (
    <div
      className="flex flex-col"
      data-onboarding-target={dataOnboardingTarget}
    >
      <div
        onContextMenu={onContextMenu}
        className="group/row relative flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 pr-8 hover:bg-n-05"
      >
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          data-state={open ? "open" : "closed"}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        >
          <RailDisclosureIcon
            open={open}
            icon={open && openIcon ? openIcon : icon}
          />
          <span className="flex-1 truncate text-base text-n-80">{label}</span>
        </button>
        {menu}
      </div>
      {open && <div className="flex flex-col pl-2">{children}</div>}
    </div>
  );
}
